import React, { useEffect, useState } from 'react';
import { getToken } from '../utils/localStorage';
import { Snackbar, Alert, Button, Box, Typography, IconButton, Collapse } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { useNavigate } from 'react-router-dom';

interface UnreadStats {
  total_unread: number;
  today_new: number;
  week_new: number;
  needs_attention: boolean;
}

interface SuggestedTime {
  suggested_hours: number[];
  reason: string;
}

const ReadingReminderNotification: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [unreadStats, setUnreadStats] = useState<UnreadStats | null>(null);
  const [suggestedTime, setSuggestedTime] = useState<SuggestedTime | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user has enabled reading reminders in localStorage
    const remindersEnabled = localStorage.getItem('reading_reminders_enabled') === 'true';
    if (!remindersEnabled) return;

    // Fetch unread stats
    const fetchUnreadStats = async () => {
      try {
        const token = getToken();
        if (!token) return;

        const response = await fetch('/api/reading/unread-stats', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setUnreadStats(data.data);

          // Show notification if there are unread articles
          if (data.data.total_unread > 0) {
            // Fetch suggested reading time
            const timeResponse = await fetch('/api/reading/suggested-time', {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            });
            if (timeResponse.ok) {
              const timeData = await timeResponse.json();
              setSuggestedTime(timeData.data);
            }

            // Check if it's a suggested reading time
            const currentHour = new Date().getHours();
            const reminderHours = JSON.parse(
              localStorage.getItem('reminder_hours') || '[9, 12, 21]',
            );

            if (reminderHours.includes(currentHour)) {
              setOpen(true);
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch unread stats:', error);
      }
    };

    fetchUnreadStats();

    // Check every hour
    const interval = setInterval(fetchUnreadStats, 60 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  const handleClose = () => {
    setOpen(false);
  };

  const handleGoToArticles = () => {
    setOpen(false);
    navigate('/articles');
  };

  const handleDismiss = () => {
    setOpen(false);
    // Snooze for 4 hours
    localStorage.setItem('reminder_snoozed_until', String(Date.now() + 4 * 60 * 60 * 1000));
  };

  if (!unreadStats || !suggestedTime) return null;

  return (
    <Snackbar open={open} anchorOrigin={{ vertical: 'top', horizontal: 'right' }} sx={{ mt: 8 }}>
      <Alert
        severity="info"
        icon={<NotificationsActiveIcon />}
        action={
          <IconButton size="small" onClick={handleClose} aria-label="关闭阅读提醒">
            <CloseIcon fontSize="small" />
          </IconButton>
        }
        sx={{ minWidth: 320 }}
      >
        <Box>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            阅读提醒
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            您有 <strong>{unreadStats.total_unread}</strong> 篇未读文章
          </Typography>
          {unreadStats.today_new > 0 && (
            <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
              今日新增: {unreadStats.today_new} 篇
            </Typography>
          )}
          {unreadStats.needs_attention && (
            <Typography variant="caption" color="warning.main" display="block" sx={{ mb: 1 }}>
              未读文章较多，建议及时阅读
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
            <Button
              size="small"
              variant="contained"
              onClick={handleGoToArticles}
              sx={{ fontSize: '0.75rem' }}
            >
              开始阅读
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={handleDismiss}
              sx={{ fontSize: '0.75rem' }}
            >
              稍后提醒
            </Button>
          </Box>
        </Box>
      </Alert>
    </Snackbar>
  );
};

export default ReadingReminderNotification;
